import { useState, useEffect } from "react";
import confetti from "canvas-confetti";
import { BOOKING_URL } from "../lib/config";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, CheckCircle2, Check, Tag, X } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

async function fetchStripePromise() {
  const res = await fetch(`${BASE}/api/stripe-config`);
  const { publishableKey } = await res.json();
  return loadStripe(publishableKey);
}

let stripePromiseCache: ReturnType<typeof fetchStripePromise> | null = null;

function getStripePromise() {
  if (!stripePromiseCache) stripePromiseCache = fetchStripePromise();
  return stripePromiseCache;
}

interface Message {
  role: "user" | "agent";
  content: string;
}

interface CheckoutScreenProps {
  agents: Array<{ name: string; price: number }>;
  messages: Message[];
  onClose: () => void;
  onGoHome: () => void;
}

interface AppliedCoupon {
  code: string;
  discountAmount: number;
  discountType: "percent" | "fixed";
  discountValue: number;
  newTotal: number;
}

function ProgressHeader({ onClose, currentStep }: { onClose: () => void; currentStep: number }) {
  return (
    <div className="sticky top-0 z-10 bg-black/70 backdrop-blur-md border-b border-cyan-400/15 px-4 py-3">
      <div className="max-w-3xl mx-auto flex items-center gap-3">
        <button
          onClick={onClose}
          className="shrink-0 flex items-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-cyan-300 transition-colors mb-4"
          title="Back to agent selection"
        >
          <ArrowLeft size={12} />
          Back
        </button>
        <div className="flex items-center flex-1">
          {(["Discovery", "Pick your Agent", "Checkout", "Kickoff"] as const).map((label, i) => {
            const stepNum = i + 1;
            const isComplete = currentStep > stepNum;
            const isActive = currentStep === stepNum;
            return (
              <div key={label} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center gap-1 shrink-0">
                  <div
                    className={[
                      "w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300",
                      isComplete
                        ? "bg-cyan-400"
                        : isActive
                        ? "border-2 border-cyan-400 bg-black"
                        : "border-2 border-slate-700 bg-black",
                    ].join(" ")}
                  >
                    {isComplete ? (
                      <Check size={12} className="text-slate-950" strokeWidth={2.5} />
                    ) : isActive ? (
                      <div className="w-2 h-2 rounded-full bg-cyan-400" />
                    ) : null}
                  </div>
                  <span
                    className={[
                      "text-[10px] font-semibold whitespace-nowrap leading-tight",
                      isComplete || isActive ? "text-cyan-300" : "text-slate-500",
                    ].join(" ")}
                  >
                    {label}
                  </span>
                </div>
                {i < 3 && (
                  <div
                    className={[
                      "flex-1 h-[2px] mx-2 mb-4 rounded-full transition-all duration-300",
                      currentStep > stepNum ? "bg-cyan-400" : "bg-slate-700",
                    ].join(" ")}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CheckoutForm({
  agents,
  transcript,
  appliedCoupon,
  onSuccess,
  onCouponInvalid,
}: {
  agents: Array<{ name: string; price: number }>;
  transcript: string;
  appliedCoupon: AppliedCoupon | null;
  onSuccess: () => void;
  onCouponInvalid: (msg: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [cardReady, setCardReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const baseTotal = agents.reduce((s, a) => s + a.price, 0);
  const discountedTotal = appliedCoupon ? appliedCoupon.newTotal : baseTotal;
  const totalDueToday = Math.round(discountedTotal * 0.5);
  const isFreeOrder = totalDueToday <= 0;

  const canSubmit = name.trim() && email.trim() && (isFreeOrder || cardReady) && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    // Stripe is only required for the paid path
    if (!isFreeOrder && (!stripe || !elements)) return;

    setSubmitting(true);
    setErrorMsg("");

    try {
      const res = await fetch(`${BASE}/api/create-payment-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          company,
          agents,
          couponCode: appliedCoupon?.code,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const isCouponInvalid = (data as { error?: string }).error === "coupon_invalid";
        if (isCouponInvalid) {
          // Coupon became invalid between apply and payment — clear it and surface the message
          onCouponInvalid((data as { message?: string }).message ?? "Your coupon is no longer valid. Please remove it and try again.");
        }
        throw new Error((data as { message?: string }).message ?? (data as { error?: string }).error ?? "Server error");
      }

      const data = await res.json();

      // Edge case: UI thought this was a free order but server disagrees
      // (e.g., coupon changed mid-session, or stale client pricing).
      // The CardElement isn't mounted, so we can't fall through to paid
      // checkout — surface a clear message and clear the coupon so the
      // user re-enters it (or pays without it).
      if (isFreeOrder && data.free !== true) {
        onCouponInvalid("Your coupon no longer fully covers this order. Please re-apply it or continue without it.");
        setSubmitting(false);
        return;
      }

      // ── Free-order path: server bypassed Stripe ──
      if (data.free === true) {
        const notifyRes = await fetch(`${BASE}/api/notify-make`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            email,
            company,
            agents,
            transcript,
            couponCode: appliedCoupon?.code,
          }),
        });
        if (!notifyRes.ok) {
          const errData = await notifyRes.json().catch(() => ({}));
          const isCouponInvalid = (errData as { error?: string }).error === "coupon_invalid";
          if (isCouponInvalid) {
            onCouponInvalid((errData as { message?: string }).message ?? "Your coupon is no longer valid. Please remove it and try again.");
          }
          throw new Error((errData as { message?: string }).message ?? "Could not finalize free order");
        }
        onSuccess();
        return;
      }

      // ── Paid path: confirm the Stripe PaymentIntent ──
      const { clientSecret } = data;

      const cardElement = elements!.getElement(CardElement);
      if (!cardElement) throw new Error("Card element not found");

      const { error, paymentIntent } = await stripe!.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: { name, email },
        },
      });

      if (error) {
        setErrorMsg(error.message ?? "Payment failed");
        setSubmitting(false);
        return;
      }

      if (paymentIntent?.status === "succeeded" || paymentIntent?.status === "requires_capture") {
        // Note: couponCode/discountAmount are intentionally omitted — server re-derives
        // them from Stripe PaymentIntent metadata to prevent client-side tampering.
        fetch(`${BASE}/api/notify-make`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            email,
            company,
            agents,
            transcript,
            paymentIntentId: paymentIntent.id,
          }),
        }).catch((err) => console.warn("[notify-make] webhook error:", err));
        onSuccess();
      } else {
        setErrorMsg("Payment was not completed. Please try again.");
        setSubmitting(false);
      }
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong");
      setSubmitting(false);
    }
  };

  const fieldClass = "w-full rounded-lg border border-cyan-400/20 bg-black/40 text-white px-3 py-2.5 text-[14px] outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/60 transition-all placeholder:text-slate-500";

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <section>
        <h2 className="text-base font-bold text-white mb-3 pb-2 border-b border-cyan-400/15">
          Contact Details
        </h2>
        <div className="flex flex-col gap-3">
          <div>
            <label htmlFor="checkout-name" className="block text-[12px] font-semibold text-slate-400 mb-1">
              Full Name <span className="text-red-400">*</span>
            </label>
            <input
              id="checkout-name"
              type="text"
              required
              placeholder="Jane Smith"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={fieldClass}
            />
          </div>
          <div>
            <label htmlFor="checkout-email" className="block text-[12px] font-semibold text-slate-400 mb-1">
              Email <span className="text-red-400">*</span>
            </label>
            <input
              id="checkout-email"
              type="email"
              required
              placeholder="jane@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={fieldClass}
            />
          </div>
          <div>
            <label htmlFor="checkout-company" className="block text-[12px] font-semibold text-slate-400 mb-1">
              Company Name <span className="text-slate-500 font-normal">(optional)</span>
            </label>
            <input
              id="checkout-company"
              type="text"
              placeholder="Acme Inc."
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className={fieldClass}
            />
          </div>
        </div>
      </section>

      {isFreeOrder ? (
        <section>
          <div className="rounded-lg border border-cyan-400/30 bg-cyan-950/20 px-4 py-4 text-[13px] text-cyan-100 leading-relaxed">
            <p className="font-semibold text-cyan-300 mb-1">No payment required</p>
            <p className="text-slate-300">
              Your coupon covers the full amount — no card needed. Click below to confirm your order.
            </p>
          </div>
          {errorMsg && (
            <p className="mt-2 text-[12px] text-red-400 font-medium">{errorMsg}</p>
          )}
        </section>
      ) : (
        <section>
          <h2 className="text-base font-bold text-white mb-3 pb-2 border-b border-cyan-400/15">
            Card Details
          </h2>
          <div className="rounded-lg border border-cyan-400/20 bg-black/40 px-3 py-3 focus-within:ring-2 focus-within:ring-cyan-400/40 focus-within:border-cyan-400/60 transition-all">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "14px",
                    color: "#f1f5f9",
                    fontFamily: "system-ui, sans-serif",
                    "::placeholder": { color: "#64748b" },
                  },
                  invalid: { color: "#ef4444" },
                },
                hidePostalCode: false,
              }}
              onChange={(e) => {
                setCardReady(e.complete);
                if (e.error) setErrorMsg(e.error.message);
                else setErrorMsg("");
              }}
            />
          </div>
          {errorMsg && (
            <p className="mt-2 text-[12px] text-red-400 font-medium">{errorMsg}</p>
          )}
        </section>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className="w-full bg-cyan-400 hover:bg-cyan-300 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 font-bold text-[15px] py-3.5 rounded-xl transition-colors active:scale-[0.99]"
        style={{ boxShadow: "0 0 24px rgba(0,229,255,0.45)" }}
      >
        {submitting
          ? "Processing…"
          : isFreeOrder
          ? "Place order — Free"
          : `Complete Order · $${totalDueToday.toLocaleString()} due today`}
      </button>

      {!isFreeOrder && (
        <p className="text-center text-[11px] text-slate-500">
          🔒 Secured by Stripe · 256-bit SSL encryption
        </p>
      )}
    </form>
  );
}

export function CheckoutScreen({ agents, messages, onClose, onGoHome }: CheckoutScreenProps) {
  const [submitted, setSubmitted] = useState(false);
  const [stripeInstance, setStripeInstance] = useState<Awaited<ReturnType<typeof loadStripe>> | null>(null);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const [couponError, setCouponError] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const transcript = messages
    .map((m) => `${m.role === "user" ? "User" : "Bluna"}: ${m.content}`)
    .join("\n\n");

  useEffect(() => {
    getStripePromise().then(setStripeInstance);
  }, []);

  useEffect(() => {
    if (!submitted) return;
    const fire = (opts: confetti.Options) =>
      confetti({ origin: { y: 0.25 }, zIndex: 9999, ...opts });
    fire({ particleCount: 60, spread: 70, startVelocity: 55, origin: { x: 0.2, y: 0.25 } });
    fire({ particleCount: 60, spread: 70, startVelocity: 55, origin: { x: 0.8, y: 0.25 } });
    const t1 = setTimeout(() => {
      fire({ particleCount: 40, spread: 100, decay: 0.91, scalar: 0.8, origin: { x: 0.5, y: 0.2 } });
    }, 200);
    const t2 = setTimeout(() => {
      fire({ particleCount: 30, spread: 120, startVelocity: 45, origin: { x: 0.5, y: 0.2 } });
    }, 400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [submitted]);

  const baseTotal = agents.reduce((s, a) => s + a.price, 0);
  const discountedTotal = appliedCoupon ? appliedCoupon.newTotal : baseTotal;
  const totalDueToday = Math.round(discountedTotal * 0.5);

  const handleApplyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    setApplyingCoupon(true);
    setCouponError("");
    try {
      const res = await fetch(`${BASE}/api/coupons/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, orderTotal: baseTotal }),
      });
      const data = await res.json();
      if (!data.valid) {
        setCouponError(data.error ?? "Coupon code not found or no longer valid.");
        setAppliedCoupon(null);
      } else {
        setAppliedCoupon({
          code: code.toUpperCase(),
          discountAmount: data.discountAmount,
          discountType: data.discountType,
          discountValue: data.discountValue,
          newTotal: data.newTotal,
        });
        setCouponInput("");
        setCouponError("");
      }
    } catch {
      setCouponError("Unable to validate coupon. Please try again.");
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponError("");
    setCouponInput("");
  };

  const currentStep = submitted ? 4 : 3;

  const fieldClass = "rounded-lg border border-cyan-400/20 bg-black/40 text-white px-3 py-2 text-[13px] outline-none focus:ring-1 focus:ring-cyan-400/40 focus:border-cyan-400/60 transition-all placeholder:text-slate-500";

  return (
    <motion.div
      key="checkout"
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="scifi-wave-bg absolute inset-0 z-30 overflow-y-auto"
    >
      <ProgressHeader onClose={onClose} currentStep={currentStep} />

      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.div
            key="confirmed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center text-center px-6 py-16 gap-8 max-w-xl mx-auto w-full"
          >
            <div className="w-16 h-16 rounded-full bg-cyan-400/10 border border-cyan-400/40 flex items-center justify-center shrink-0" style={{ boxShadow: "0 0 24px rgba(0,229,255,0.35)" }}>
              <CheckCircle2 className="text-cyan-300 w-9 h-9" />
            </div>

            <div className="flex flex-col gap-3">
              <h2 className="text-2xl font-bold text-white">You're all set!</h2>
              <p className="text-slate-300 text-base leading-relaxed">
                Thanks for trusting us with your automation. You've just taken the first step toward a more autonomous business. Your 50% implementation deposit has been securely received, and our architects are already being briefed with your project.
              </p>
              {appliedCoupon && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-cyan-400/30 bg-cyan-950/30 px-3 py-2.5 text-[13px]">
                  <Tag className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                  <span className="text-slate-300">Coupon</span>
                  <span className="font-mono font-bold text-cyan-300 break-all">{appliedCoupon.code}</span>
                  <span className="text-slate-400">applied —</span>
                  <span className="font-semibold text-cyan-300">saved ${appliedCoupon.discountAmount.toLocaleString()}</span>
                </div>
              )}
            </div>

            <div className="w-full text-left flex flex-col gap-5">
              <h3 className="text-[15px] font-bold text-cyan-300 uppercase tracking-wide">
                What happens in the next 24 hours:
              </h3>
              {[
                {
                  step: "1",
                  content: (
                    <>
                      <a
                        href={BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-cyan-300 hover:text-cyan-200 underline underline-offset-2 transition-colors"
                      >
                        Schedule a call
                      </a>{" "}
                      or wait to be contacted within 24 hours.
                    </>
                  ),
                },
                {
                  step: "2",
                  content: (
                    <>
                      <span className="font-semibold text-white">Gather specific requirements:</span>{" "}
                      one of our engineers will deep dive to look at your specific requirements and will start designing and building your agent.
                    </>
                  ),
                },
                {
                  step: "3",
                  content: (
                    <>
                      <span className="font-semibold text-white">The Build Begins:</span>{" "}
                      through iterations, the agent will be built completely tailored to your needs.
                    </>
                  ),
                },
                {
                  step: "4",
                  content: (
                    <>
                      You didn't just buy an agent;{" "}
                      <span className="font-semibold text-white">you bought back your time.</span>{" "}
                      Relax, we've got the heavy lifting from here.
                    </>
                  ),
                },
              ].map(({ step, content }) => (
                <div key={step} className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-full bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 flex items-center justify-center text-[14px] font-bold shrink-0 mt-0.5">
                    {step}
                  </div>
                  <p className="text-[15px] text-slate-300 pt-1.5 leading-relaxed">{content}</p>
                </div>
              ))}
            </div>

            <button
              onClick={onGoHome}
              className="mt-2 text-sm font-semibold text-cyan-300 hover:text-cyan-200 underline underline-offset-2 transition-colors"
            >
              Return to Agents To Go
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-5xl mx-auto px-4 sm:px-8 py-8 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12"
          >
            {/* ── LEFT: Payment ── */}
            {stripeInstance ? (
              <Elements stripe={stripeInstance}>
                <CheckoutForm
                  agents={agents}
                  transcript={transcript}
                  appliedCoupon={appliedCoupon}
                  onSuccess={() => setSubmitted(true)}
                  onCouponInvalid={(msg) => {
                    setAppliedCoupon(null);
                    setCouponError(msg);
                  }}
                />
              </Elements>
            ) : (
              <div className="flex items-center justify-center py-20 text-slate-400 text-sm">
                Loading payment form…
              </div>
            )}

            {/* ── RIGHT: Order Summary ── */}
            <div className="flex flex-col gap-6">
              <section>
                <h2 className="text-base font-bold text-white mb-3 pb-2 border-b border-cyan-400/15">
                  Order Summary
                </h2>
                <div className="rounded-xl border border-cyan-400/15 overflow-hidden bg-black/40 backdrop-blur-sm overflow-x-auto">
                  <table className="w-full text-[13px] min-w-[320px]">
                    <thead>
                      <tr className="bg-purple-950/40 text-slate-400 font-semibold uppercase text-[11px] tracking-wide">
                        <th className="text-left px-4 py-2.5">Agent</th>
                        <th className="text-right px-4 py-2.5">Price</th>
                        <th className="text-right px-4 py-2.5 text-cyan-300">Due Today</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agents.map((agent, i) => (
                        <tr
                          key={agent.name}
                          className={i % 2 === 0 ? "bg-transparent" : "bg-purple-950/20"}
                          style={{ borderTop: "1px solid rgba(0,229,255,0.10)" }}
                        >
                          <td className="px-4 py-3 font-semibold text-white">{agent.name}</td>
                          <td className="px-4 py-3 text-right text-slate-300">
                            ${agent.price.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-cyan-300">
                            ${Math.round(agent.price * 0.5).toLocaleString()}
                          </td>
                        </tr>
                      ))}

                      {appliedCoupon && (
                        <tr style={{ borderTop: "1px solid rgba(0,229,255,0.10)" }} className="bg-cyan-950/20">
                          <td className="px-4 py-2.5 text-cyan-300 flex items-center gap-1.5 text-[12px]">
                            <Tag className="w-3 h-3 shrink-0" />
                            <span className="font-mono font-bold">{appliedCoupon.code}</span>
                            <span className="text-slate-400 font-normal">
                              {appliedCoupon.discountType === "percent"
                                ? `(${appliedCoupon.discountValue}% off)`
                                : `($${appliedCoupon.discountValue.toLocaleString()} off)`}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right text-cyan-300 font-semibold text-[13px]">
                            −${appliedCoupon.discountAmount.toLocaleString()}
                          </td>
                          <td className="px-4 py-2.5 text-right text-cyan-300 font-semibold text-[13px]">
                            −${Math.round(appliedCoupon.discountAmount * 0.5).toLocaleString()}
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: "2px solid rgba(0,229,255,0.30)" }} className="bg-purple-950/40 font-bold">
                        <td className="px-4 py-3 text-white">Total</td>
                        <td className="px-4 py-3 text-right">
                          {appliedCoupon ? (
                            <span className="flex flex-col items-end gap-0.5">
                              <span className="line-through text-slate-500 text-[11px] font-normal">${baseTotal.toLocaleString()}</span>
                              <span className="text-white">${discountedTotal.toLocaleString()}</span>
                            </span>
                          ) : (
                            <span className="text-white">${baseTotal.toLocaleString()}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-cyan-300">
                          ${totalDueToday.toLocaleString()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Coupon input */}
                <div className="mt-3">
                  {appliedCoupon ? (
                    <div className="flex items-start justify-between gap-2 rounded-lg border border-cyan-400/30 bg-cyan-950/30 px-3 py-2">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px] min-w-0">
                        <Tag className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                        <span className="font-mono font-bold text-cyan-300 break-all">{appliedCoupon.code}</span>
                        <span className="text-slate-400">applied — saving ${appliedCoupon.discountAmount.toLocaleString()}</span>
                      </div>
                      <button
                        onClick={removeCoupon}
                        className="text-slate-400 hover:text-red-400 transition-colors shrink-0 mt-1"
                        title="Remove coupon"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1.5">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          aria-label="Coupon code"
                          value={couponInput}
                          onChange={(e) => { setCouponInput(e.target.value); setCouponError(""); }}
                          onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                          placeholder="Coupon code"
                          className={fieldClass + " flex-1 uppercase"}
                        />
                        <button
                          onClick={handleApplyCoupon}
                          disabled={!couponInput.trim() || applyingCoupon}
                          className="px-4 py-2 rounded-lg text-[13px] font-semibold bg-cyan-400/15 border border-cyan-400/30 text-cyan-300 hover:bg-cyan-400/25 hover:border-cyan-400/50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        >
                          {applyingCoupon ? "…" : "Apply"}
                        </button>
                      </div>
                      {couponError && (
                        <p className="text-[12px] text-red-400 font-medium">{couponError}</p>
                      )}
                    </div>
                  )}
                </div>
              </section>

              <div className="rounded-xl bg-purple-950/40 border border-cyan-400/20 px-4 py-3.5 text-[13px] leading-relaxed text-slate-200 backdrop-blur-sm">
                <p>
                  Remaining 50% is due upon project completion.{" "}
                  <span className="font-bold text-fuchsia-300">
                    100% satisfaction guaranteed or your money back.
                  </span>
                </p>
              </div>

              <div className="flex flex-col gap-4">
                <h3 className="text-[15px] font-bold text-cyan-300 uppercase tracking-wide">
                  What happens next
                </h3>
                {[
                  { step: "1", label: "Confirmation email sent immediately" },
                  { step: "2", label: "Kickoff call scheduled within 24 hours" },
                  { step: "3", label: "Agent delivered in 5–10 business days" },
                ].map(({ step, label }) => (
                  <div key={step} className="flex items-start gap-4">
                    <div className="w-9 h-9 rounded-full bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 flex items-center justify-center text-[14px] font-bold shrink-0 mt-0.5">
                      {step}
                    </div>
                    <p className="text-[15px] text-slate-300 pt-1.5">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
